'use client';

import { ConnectWithStrava } from './VendorLogos';
import { FcSynchronize } from 'react-icons/fc';
import Loading from '@/app/components/Loading';
import { toast } from 'react-toastify';
import { trpc } from '@/app/api/trpc/client';
import { stravaApi } from '@trainme/api/client';
import { useState } from 'react';
import { getVendorCardClassName } from './vendorCardStyles';

// Strava's Developer Program requires the app owner's account to have an
// active Strava subscription (rolled out June 2026). The app was
// deactivated when that lapsed (Aug 2026) and this flag hid the
// connect/sync UI while it was down; the subscription is active again as
// of 2026-08-21, confirmed via a direct call to Strava's API.
export const STRAVA_INTEGRATION_UNAVAILABLE = false;

export default function Strava() {
  const {
    data: connected,
    isError: connectedError,
    refetch,
  } = trpc.strava.connected.useQuery();
  const { mutate: disconnect } = trpc.strava.disconnect.useMutation({
    onSuccess: () => {
      refetch();
      toast.success('Disconnected from Strava.');
    },
  });
  const { mutateAsync: syncStrava, isPending } =
    trpc.strava.syncStrava.useMutation({
      onSuccess: (data) => {
        const count = data?.count ?? 0;
        toast.success(
          `Synced ${count} Strava ${count === 1 ? 'activity' : 'activities'}.`
        );
      },
      onError: (error) => {
        toast.error('Sync failed.', error);
      },
    });

  const [syncDataFromDaysAgo, setSyncDataFromDaysAgo] = useState(1);

  const handleConnect = () => {
    window.location.href = getAuthUrl('settings');
  };

  return (
    <div className={getVendorCardClassName(connected === true)}>
      <div className='flex gap-4 items-center'>
        <h2 className='h2'>Strava Connect</h2>
      </div>
      <div className='py-4'>
        {STRAVA_INTEGRATION_UNAVAILABLE ? (
          <p className='text-sm text-gray-500 dark:text-gray-400'>
            Strava sync is temporarily unavailable while we resolve an account
            issue on our end. This isn&apos;t something wrong with your account
            &mdash; we&apos;ll be back soon.
          </p>
        ) : (
          <>
            <div className='flex gap-4'>
              {connected === false && (
                <button
                  className='hover:opacity-80 transition-opacity'
                  onClick={handleConnect}
                >
                  <ConnectWithStrava />
                </button>
              )}
              {connected === true && (
                <button
                  className='btn btn-secondary text-red-500'
                  onClick={() => disconnect()}
                >
                  Disconnect Strava
                </button>
              )}
              {connectedError && (
                <p className='text-red-500'>
                  Couldn&apos;t check Strava connection status. Please refresh
                  the page.
                </p>
              )}
              {!connectedError &&
                (connected === null || connected === undefined) && (
                  <div className='flex items-center gap-2'>
                    <span>Connecting to Strava...</span>
                    <Loading size={14} />
                  </div>
                )}
            </div>
            {connected === true && (
              <div className='mt-4 w-full space-y-4'>
                <p className='text-sm font-normal text-gray-600 dark:text-gray-400'>
                  Sync Data From:
                </p>
                <div className='flex gap-3 flex-wrap'>
                  {[
                    { value: 1, label: 'Today' },
                    { value: 7, label: 'Last Week' },
                    { value: 31, label: 'Last Month' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      type='button'
                      onClick={() => setSyncDataFromDaysAgo(value)}
                      className={`text-base font-medium transition ${
                        syncDataFromDaysAgo === value
                          ? 'text-emerald-600 dark:text-emerald-400 underline'
                          : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  className='btn btn-secondary'
                  onClick={() =>
                    syncStrava({ fromDaysAgo: syncDataFromDaysAgo })
                  }
                  disabled={isPending}
                >
                  Sync Strava Data
                  {isPending && <FcSynchronize className='icon loading-icon' />}
                </button>
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                  When your activity is uploaded to Strava, we will
                  automatically update your activity here. For historical data,
                  you can manually backfill your activities from a specific
                  period above.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export const getAuthUrl = (origin: 'chat' | 'settings' = 'chat') => {
  const clientId = process.env.NEXT_PUBLIC_STRAVA_CLIENT_ID || '';

  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || '';
  const redirectUri = `${baseUrl}/vendors/strava/authorize`;
  const scope = 'profile:read_all,activity:read_all';

  const stravaAuthUrl = stravaApi.authorizeUrl(
    clientId,
    redirectUri,
    scope,
    origin
  );

  return stravaAuthUrl;
};
